import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import ProposalStatusBadge from "@/components/proposals/ProposalStatusBadge";
import { ClientInfoCard } from "@/components/clients/ClientInfoCard";
import {
  Loader2,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Download,
  Package,
  Calendar,
  Clock,
  AlertTriangle,
  ClipboardCheck,
  Link2,
  ArrowUpRight,
  Share2,
  Check,
  Trash2,
  Users,
  Hotel,
  Phone,
  Save,
  StickyNote,
  Plus,
  X,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import {
  deleteBookingServiceTicketFile,
  downloadBookingServiceTicketFile,
  finalizeBookingPackage,
  getBookingPackage,
  updateBookingPackageInfo,
  updateBookingServiceNote,
  updateBookingServiceStatus,
  uploadBookingServiceTicket,
} from "@/lib/bookingPackagesApi";
import { downloadProposalInvoiceFile } from "@/lib/proposalsApi";

type ReservationStatus = "pending_decision" | "needs_reservation" | "reservation_attached" | "no_reservation_required";

interface BookingServiceTicketFile {
  id: string;
  file_name: string | null;
  uploaded_at: string | null;
  download_url: string | null;
}

interface BookingService {
  id: string;
  service_type: string;
  service_name: string;
  service_date: string | null;
  service_time: string | null;
  service_description: string | null;
  ticket_type: string | null;
  quantity: number | null;
  unit_value: number | null;
  total_value: number | null;
  internal_notes: string | null;
  public_note: string | null;
  no_ticket_required: boolean;
  ticket_file_name: string | null;
  ticket_uploaded_at: string | null;
  status: string;
  service_data: any;
  reservation_status: ReservationStatus;
  ticket_download_url: string | null;
  ticket_files: BookingServiceTicketFile[] | null;
}

// Orders services chronologically with the oldest first. Services without a
// date are pushed to the end.
const getServiceTimestamp = (service: BookingService): number => {
  if (!service.service_date) return Number.POSITIVE_INFINITY;
  const time = service.service_time && service.service_time.length >= 4 ? service.service_time.slice(0, 5) : "00:00";
  const parsed = new Date(`${service.service_date}T${time}`).getTime();
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

const sortServicesChronologically = (list: BookingService[]): BookingService[] =>
  [...list].sort((a, b) => getServiceTimestamp(a) - getServiceTimestamp(b));

interface BookingPackageData {
  id: string;
  proposal_id: string;
  sales_owner_id: string | null;
  manager_id: string | null;
  operations_owner_id: string | null;
  status: string;
  notes: string | null;
  guests?: string | null;
  hotel?: string | null;
  contact?: string | null;
  created_at: string;
  proposal?: {
    title: string;
    main_destination: string;
    currency?: string | null;
    pax_adults: number;
    pax_children: number;
    pax_infants: number;
    status: string;
    clients?: {
      first_name?: string;
      last_name?: string;
      email?: string;
      mobile?: string;
      alternate_phone?: string;
      alternatePhone?: string;
      company?: string;
      partner_id?: number | string | null;
      partnerId?: number | string | null;
      partner_business_type?: string;
      partnerBusinessType?: string;
      operator_agency_name?: string;
      operatorAgencyName?: string;
      operator_agent_name?: string;
      operatorAgentName?: string;
    };
  };
}

const BookingPackagePage: React.FC = () => {
  const { id } = useParams();
  const { userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookingPackage, setBookingPackage] = useState<BookingPackageData | null>(null);
  const [services, setServices] = useState<BookingService[]>([]);
  const [uploadingServiceId, setUploadingServiceId] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [draggingServiceId, setDraggingServiceId] = useState<string | null>(null);
  const [showConfirmChangeModal, setShowConfirmChangeModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    service: BookingService;
    newStatus: ReservationStatus;
  } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [infoForm, setInfoForm] = useState({ guests: "", hotel: "", contact: "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [noteEditorServiceId, setNoteEditorServiceId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNoteId, setSavingNoteId] = useState<string | null>(null);
  const [downloadingProposalInvoice, setDownloadingProposalInvoice] = useState(false);
  const reservationInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const { toast } = useToast();

  const canManageReservations = userRole === "ceo" || userRole === "general_manager" || userRole === "finance_staff" || userRole === "sales_staff";

  useEffect(() => {
    if (id) {
      fetchBookingPackage();
    }
  }, [id]);

  const fetchBookingPackage = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const data = await getBookingPackage(id);
      setBookingPackage(data as BookingPackageData);
      setInfoForm({
        guests: data.guests ?? "",
        hotel: data.hotel ?? "",
        contact: data.contact ?? "",
      });
      const servicesData = data.services || [];
      const normalizedServices = servicesData
          .filter((s: BookingService) => (s?.service_type || "").toLowerCase() !== "handling_fee")
          .map((s: BookingService) => ({
            ...s,
            reservation_status: s.reservation_status as ReservationStatus,
            ticket_files: s.ticket_files ?? [],
          }));

      setServices(sortServicesChronologically(normalizedServices));
    } catch (error: any) {
      toast({
        title: "Error loading booking package",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (service: BookingService, newStatus: ReservationStatus) => {
    if (
      (service.reservation_status === "no_reservation_required" || 
       service.reservation_status === "reservation_attached") &&
      newStatus !== service.reservation_status
    ) {
      setPendingStatusChange({ service, newStatus });
      setShowConfirmChangeModal(true);
      return;
    }

    await updateServiceStatus(service, newStatus);
  };

  const updateServiceStatus = async (service: BookingService, newStatus: ReservationStatus) => {
    try {
      const updated = await updateBookingServiceStatus(service.id, newStatus);

      setServices((prev) =>
        prev.map((s) =>
          s.id === service.id
            ? (() => {
                const nextService: BookingService = {
                  ...s,
                  ...updated,
                  reservation_status: updated.reservation_status as ReservationStatus,
                };

                if (newStatus !== "reservation_attached") {
                  nextService.ticket_file_name = null;
                  nextService.ticket_uploaded_at = null;
                  nextService.ticket_download_url = null;
                  nextService.ticket_files = [];
                }

                if (newStatus === "no_reservation_required") {
                  nextService.no_ticket_required = true;
                } else if (newStatus === "needs_reservation") {
                  nextService.no_ticket_required = false;
                }

                return nextService;
              })()
            : s
        )
      );

      const statusLabels: Record<ReservationStatus, string> = {
        pending_decision: "Pending Decision",
        needs_reservation: "Needs Reservation",
        reservation_attached: "Reservation Attached",
        no_reservation_required: "No Reservation Required",
      };

      toast({
        title: `Status updated: ${statusLabels[newStatus]}`,
      });
    } catch (error: any) {
      toast({
        title: "Error updating status",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleConfirmStatusChange = async () => {
    if (pendingStatusChange) {
      await updateServiceStatus(pendingStatusChange.service, pendingStatusChange.newStatus);
    }
    setShowConfirmChangeModal(false);
    setPendingStatusChange(null);
  };

  const validateReservationFile = (file: File): string | null => {
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      return "Only PDF files are allowed.";
    }
    if (file.size > 10 * 1024 * 1024) {
      return "Maximum file size is 10MB.";
    }
    return null;
  };

  const openInlineReservationPicker = (serviceId: string) => {
    reservationInputRefs.current[serviceId]?.click();
  };

  const handleUploadReservation = async (service: BookingService, fileList: FileList | File[] | null) => {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length === 0) return;

    for (const file of files) {
      const validationError = validateReservationFile(file);
      if (validationError) {
        toast({
          title: "Invalid file",
          description: `${file.name}: ${validationError}`,
          variant: "destructive",
        });
        return;
      }
    }

    if (uploadingServiceId === service.id) return;

    try {
      setUploadingServiceId(service.id);
      await uploadBookingServiceTicket(service.id, files);
      await fetchBookingPackage();

      toast({
        title: files.length > 1 ? `${files.length} reservations uploaded successfully` : "Reservation uploaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploadingServiceId(null);
      setDraggingServiceId(null);
    }
  };

  const handleDownloadReservation = async (service: BookingService, ticketFile: BookingServiceTicketFile) => {
    try {
      const { blob, fileName } = await downloadBookingServiceTicketFile(service.id, ticketFile.id);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName || ticketFile.file_name || "reservation.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error: any) {
      toast({
        title: "Download failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteReservationFile = async (service: BookingService, ticketFile: BookingServiceTicketFile) => {
    if (deletingFileId) return;
    try {
      setDeletingFileId(ticketFile.id);
      const updated = await deleteBookingServiceTicketFile(service.id, ticketFile.id);
      setServices((prev) =>
        prev.map((s) =>
          s.id === service.id
            ? {
                ...s,
                ...updated,
                reservation_status: updated.reservation_status as ReservationStatus,
                ticket_files: updated.ticket_files ?? [],
              }
            : s
        )
      );
      toast({ title: "File removed" });
    } catch (error: any) {
      toast({
        title: "Could not remove file",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDownloadProposalInvoice = async () => {
    const proposalId = bookingPackage?.proposal_id;
    if (!proposalId) {
      toast({
        title: "Invoice not available",
        description: "This booking package has no linked proposal invoice.",
      });
      return;
    }

    try {
      setDownloadingProposalInvoice(true);
      const { blob, fileName } = await downloadProposalInvoiceFile(proposalId);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName || `proposal-invoice-${proposalId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error: any) {
      toast({
        title: "Download failed",
        description: error?.message || "Could not download the proposal invoice.",
        variant: "destructive",
      });
    } finally {
      setDownloadingProposalInvoice(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/booking/${bookingPackage?.id}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    toast({ title: "Link copied to clipboard!" });
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const openNoteEditor = (service: BookingService) => {
    setNoteEditorServiceId(service.id);
    setNoteDraft(service.public_note ?? "");
  };

  const cancelNoteEditor = () => {
    setNoteEditorServiceId(null);
    setNoteDraft("");
  };

  const handleSaveNote = async (service: BookingService) => {
    try {
      setSavingNoteId(service.id);
      const trimmed = noteDraft.trim();
      const updated = await updateBookingServiceNote(service.id, trimmed || null);
      setServices((prev) =>
        prev.map((s) => (s.id === service.id ? { ...s, public_note: updated.public_note ?? null } : s))
      );
      setNoteEditorServiceId(null);
      setNoteDraft("");
      toast({ title: trimmed ? "Note saved" : "Note removed" });
    } catch (error: any) {
      toast({
        title: "Could not save note",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSavingNoteId(null);
    }
  };

  const handleSaveInfo = async () => {
    if (!bookingPackage) return;
    try {
      setSavingInfo(true);
      const updated = await updateBookingPackageInfo(bookingPackage.id, {
        guests: infoForm.guests.trim() || null,
        hotel: infoForm.hotel.trim() || null,
        contact: infoForm.contact.trim() || null,
      });
      setBookingPackage((prev) => (prev ? { ...prev, ...updated } as BookingPackageData : prev));
      setInfoForm({
        guests: updated.guests ?? "",
        hotel: updated.hotel ?? "",
        contact: updated.contact ?? "",
      });
      toast({ title: "Booking details saved" });
    } catch (error: any) {
      toast({
        title: "Could not save details",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSavingInfo(false);
    }
  };

  const handleFinishBooking = async () => {
    if (!allComplete || !bookingPackage) return;

    try {
      setIsFinishing(true);
      await finalizeBookingPackage(bookingPackage.id);

      toast({
        title: "Booking completed!",
        description: "All reservations have been finalized and the package is ready.",
      });

      await fetchBookingPackage();
    } catch (error: any) {
      toast({
        title: "Error completing booking",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsFinishing(false);
    }
  };

  const getServiceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      tour: "Tour",
      attraction: "Attraction",
      transfer: "Transfer",
      airport_service: "Airport Service",
      accommodation: "Accommodation",
      additional_request: "Additional Service",
      flight: "Flight",
    };
    return labels[type] || type;
  };

  const getReservationStatusBadge = (status: ReservationStatus) => {
    switch (status) {
      case "reservation_attached":
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Reservation Attached
          </Badge>
        );
      case "no_reservation_required":
        return (
          <Badge variant="secondary" className="bg-slate-100 text-slate-700">
            <XCircle className="h-3 w-3 mr-1" />
            No Reservation Required
          </Badge>
        );
      case "needs_reservation":
        return (
          <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">
            <Upload className="h-3 w-3 mr-1" />
            Needs Reservation
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
            <Clock className="h-3 w-3 mr-1" />
            Pending Decision
          </Badge>
        );
    }
  };

  const completedServices = services.filter(
    (s) => s.reservation_status === "reservation_attached" || s.reservation_status === "no_reservation_required"
  ).length;
  const totalServices = services.length;
  const completionPercentage = totalServices > 0 ? (completedServices / totalServices) * 100 : 0;
  const allComplete = completedServices === totalServices && totalServices > 0;

  const hasIncompleteNeeds = services.some(
    (s) => s.reservation_status === "needs_reservation" && !s.ticket_file_name
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!bookingPackage) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Booking Package Not Found</h2>
          <p className="text-muted-foreground">The requested booking package does not exist.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!canManageReservations) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">Only Sales, Finance team, Managers, and CEO can access booking package management.</p>
        </div>
      </DashboardLayout>
    );
  }

  const proposal = bookingPackage.proposal;
  const proposalCurrency = proposal?.currency || "USD";
  const formatMoney = (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: proposalCurrency }).format(value);
    } catch {
      return `$${value.toFixed(2)}`;
    }
  };
  const client = proposal?.clients;
  const paxAdults = proposal?.pax_adults || 0;
  const paxChildren = proposal?.pax_children || 0;
  const paxInfants = proposal?.pax_infants || 0;
  const totalPassengers = paxAdults + paxChildren + paxInfants;
  const publicLink = `${window.location.origin}/booking/${bookingPackage.id}`;

  return (
    <DashboardLayout>
      <div className="mx-auto w-full max-w-[1520px] space-y-6 pb-2">
        <Card className="border-border/70 bg-gradient-to-br from-card via-card to-muted/20 shadow-sm">
          <CardContent className="p-5 md:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
                    <Package className="h-6 w-6 text-primary" />
                    Booking Package
                  </h1>
                  <ProposalStatusBadge status={bookingPackage.status} size="md" />
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  {proposal?.title || "Untitled"} • {proposal?.main_destination}
                </p>
                {(bookingPackage.guests || bookingPackage.hotel || bookingPackage.contact) && (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {bookingPackage.guests && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/80 px-3 py-1 text-sm">
                        <Users className="h-3.5 w-3.5 text-primary" />
                        <span className="text-muted-foreground">Guests:</span>
                        <span className="font-medium text-foreground">{bookingPackage.guests}</span>
                      </span>
                    )}
                    {bookingPackage.hotel && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/80 px-3 py-1 text-sm">
                        <Hotel className="h-3.5 w-3.5 text-primary" />
                        <span className="text-muted-foreground">Hotel:</span>
                        <span className="font-medium text-foreground">{bookingPackage.hotel}</span>
                      </span>
                    )}
                    {bookingPackage.contact && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/80 px-3 py-1 text-sm">
                        <Phone className="h-3.5 w-3.5 text-primary" />
                        <span className="text-muted-foreground">Contact:</span>
                        <span className="font-medium text-foreground">{bookingPackage.contact}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                onClick={handleDownloadProposalInvoice}
                disabled={downloadingProposalInvoice}
                className="gap-2 self-start border-border/70 bg-background hover:bg-muted/60"
              >
                {downloadingProposalInvoice ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 text-primary" />
                )}
                Download Proposal Invoice
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className={client ? "grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] xl:items-stretch" : ""}>
          <Card className="h-full border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="h-5 w-5 text-primary" />
                Share with Client
              </CardTitle>
              <CardDescription>
                Share this link with the client to view their booking details and download reservations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-3">
                <div className="bg-background border rounded-lg px-4 py-2.5 font-mono text-sm truncate" title={publicLink}>
                  {publicLink}
                </div>
                <div className="grid grid-cols-1 min-[460px]:grid-cols-2 gap-2 min-[460px]:max-w-[360px] min-[460px]:ml-auto">
                  <Button
                    onClick={handleCopyLink}
                    variant="outline"
                    className={`w-full justify-center gap-2 border ${
                      linkCopied
                        ? "border-emerald-500/40 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        : "border-border bg-background text-foreground hover:bg-muted/60"
                    }`}
                  >
                    {linkCopied ? (
                      <>
                        <Check className="h-4 w-4" />
                        Link Copied
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4" />
                        Copy Link
                      </>
                    )}
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => window.open(publicLink, "_blank")}
                    className="w-full justify-center gap-2 bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                  >
                    <ArrowUpRight className="h-4 w-4" />
                    Preview
                  </Button>
                </div>
              </div>

              <div className="border-t border-border/60 pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className={`h-4 w-4 ${allComplete ? "text-emerald-600" : "text-amber-600"}`} />
                    <span className="text-sm font-semibold">Reservation Progress</span>
                  </div>
                  <span className={`text-sm font-medium ${allComplete ? "text-emerald-700" : "text-amber-700"}`}>
                    {completedServices} / {totalServices}
                  </span>
                </div>

                <div
                  className={`mt-3 rounded-xl border px-4 py-3 ${
                    allComplete ? "border-emerald-300/60 bg-emerald-50/40" : "border-amber-300/60 bg-amber-50/40"
                  }`}
                >
                  <Progress value={completionPercentage} className="h-2" />
                  {!allComplete && (
                    <p className="text-sm text-muted-foreground mt-3">
                      {hasIncompleteNeeds ? (
                        <span className="text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4" />
                          Some services marked as "Needs Reservation" are missing PDF uploads.
                        </span>
                      ) : (
                        "Each service must have a reservation attached or be marked as 'No Reservation Required'."
                      )}
                    </p>
                  )}
                  {allComplete && (
                    <div className="flex flex-col gap-3 mt-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm text-emerald-700 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        All services have been processed. Ready to finalize!
                      </p>
                      <Button
                        onClick={handleFinishBooking}
                        disabled={isFinishing || bookingPackage.status === "booked"}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {isFinishing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Finalizing...
                          </>
                        ) : bookingPackage.status === "booked" ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Booking Finalized
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Finalize Booking
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {client && (
            <ClientInfoCard
              client={client}
              className="mt-0 h-full border-border/70 bg-gradient-to-br from-card to-muted/20 shadow-sm"
              footer={
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-lg border border-border/60 bg-background/80 px-2.5 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Adults</p>
                    <p className="text-base font-semibold">{paxAdults}</p>
                  </div>
                  <div className="rounded-lg border border-border/60 bg-background/80 px-2.5 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Children</p>
                    <p className="text-base font-semibold">{paxChildren}</p>
                  </div>
                  <div className="rounded-lg border border-border/60 bg-background/80 px-2.5 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Infants</p>
                    <p className="text-base font-semibold">{paxInfants}</p>
                  </div>
                  <div className="rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-primary/80">Total</p>
                    <p className="text-base font-bold text-primary">{totalPassengers}</p>
                  </div>
                </div>
              }
            />
          )}
        </div>

        <Card className="border-border/70 bg-card shadow-sm">
          <CardHeader className="border-b border-border/60 pb-4">
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              Booking Confirmation Details
            </CardTitle>
            <CardDescription>
              Fill in the guests, hotel and contact. Any field you fill is shown as a header on the client's booking confirmation.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="booking-guests" className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-primary" />
                  Guests
                </Label>
                <Input
                  id="booking-guests"
                  value={infoForm.guests}
                  placeholder="e.g. John & Jane Doe (2 adults)"
                  onChange={(e) => setInfoForm((prev) => ({ ...prev, guests: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="booking-hotel" className="flex items-center gap-1.5">
                  <Hotel className="h-3.5 w-3.5 text-primary" />
                  Hotel
                </Label>
                <Input
                  id="booking-hotel"
                  value={infoForm.hotel}
                  placeholder="e.g. Burj Al Arab, Deluxe Suite"
                  onChange={(e) => setInfoForm((prev) => ({ ...prev, hotel: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="booking-contact" className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-primary" />
                  Contact
                </Label>
                <Input
                  id="booking-contact"
                  value={infoForm.contact}
                  placeholder="e.g. +971 50 123 4567"
                  onChange={(e) => setInfoForm((prev) => ({ ...prev, contact: e.target.value }))}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleSaveInfo}
                disabled={
                  savingInfo ||
                  ((infoForm.guests.trim() || "") === (bookingPackage.guests ?? "") &&
                    (infoForm.hotel.trim() || "") === (bookingPackage.hotel ?? "") &&
                    (infoForm.contact.trim() || "") === (bookingPackage.contact ?? ""))
                }
                className="gap-2"
              >
                {savingInfo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save details
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/70 bg-card shadow-sm">
          <CardHeader className="border-b border-border/60 pb-4">
            <CardTitle className="flex items-center justify-between">
              <span>Services ({services.length})</span>
            </CardTitle>
            <CardDescription>
              For each service, indicate whether a reservation is required and upload the confirmation PDF.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {services.map((service) => (
                <div
                  key={service.id}
                  className={`rounded-xl border p-4 md:p-5 transition-colors ${
                    service.reservation_status === "reservation_attached"
                      ? "bg-emerald-50/35 border-emerald-200/80"
                      : service.reservation_status === "no_reservation_required"
                      ? "bg-slate-50/70 border-slate-200/90"
                      : service.reservation_status === "needs_reservation"
                      ? "bg-blue-50/35 border-blue-200/80"
                      : "bg-amber-50/35 border-amber-200/80"
                  }`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Badge variant="outline" className="border-border/70 bg-background/80 text-foreground/90">
                          {getServiceTypeLabel(service.service_type)}
                        </Badge>
                        {getReservationStatusBadge(service.reservation_status)}
                      </div>
                      <h4 className="text-lg font-semibold tracking-tight">{service.service_name}</h4>
                      {service.service_date && (
                        <p className="mt-1.5 text-sm text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(service.service_date), "MMM d, yyyy")}
                          {service.service_time && ` at ${service.service_time.slice(0, 5)}`}
                        </p>
                      )}
                      {service.service_description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {service.service_description}
                        </p>
                      )}
                      {(service.ticket_type || service.quantity != null || service.total_value != null) && (
                        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                          {service.ticket_type && (
                            <span className="text-foreground/90">
                              <span className="text-muted-foreground">Ticket type:</span>{" "}
                              <span className="font-medium">{service.ticket_type}</span>
                            </span>
                          )}
                          {service.quantity != null && (
                            <span className="text-foreground/90">
                              <span className="text-muted-foreground">Qty:</span>{" "}
                              <span className="font-medium">{service.quantity}</span>
                            </span>
                          )}
                          {service.total_value != null && (
                            <span className="text-foreground/90">
                              <span className="text-muted-foreground">Value:</span>{" "}
                              <span className="font-medium">{formatMoney(service.total_value)}</span>
                              {service.unit_value != null && service.quantity != null && service.quantity > 1 && (
                                <span className="text-muted-foreground">
                                  {" "}({formatMoney(service.unit_value)} × {service.quantity})
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      )}

                      {canManageReservations && (
                        <div className="mt-3">
                          {noteEditorServiceId === service.id ? (
                            <div className="rounded-lg border border-amber-200/80 bg-amber-50/40 p-3 space-y-2">
                              <div className="flex items-center gap-1.5 text-sm font-medium text-amber-800">
                                <StickyNote className="h-3.5 w-3.5" />
                                Note for the client
                              </div>
                              <Textarea
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="This note will appear on the client's booking confirmation for this service."
                                rows={3}
                                className="bg-white/90"
                              />
                              <div className="flex items-center justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={cancelNoteEditor} className="gap-1.5">
                                  <X className="h-3.5 w-3.5" />
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveNote(service)}
                                  disabled={savingNoteId === service.id}
                                  className="gap-1.5"
                                >
                                  {savingNoteId === service.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Save className="h-3.5 w-3.5" />
                                  )}
                                  Save note
                                </Button>
                              </div>
                            </div>
                          ) : service.public_note ? (
                            <div className="rounded-lg border border-amber-200/80 bg-amber-50/50 p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-1.5 text-sm text-amber-900">
                                  <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
                                  <span className="whitespace-pre-wrap">{service.public_note}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => openNoteEditor(service)}
                                  className="shrink-0 text-xs font-medium text-amber-700 underline-offset-2 hover:underline"
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openNoteEditor(service)}
                              className="gap-1.5 border-dashed border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add note
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {canManageReservations && (
                      <div className="flex flex-col gap-3 min-w-[300px] lg:max-w-[360px]">
                        <div className="text-sm font-medium text-muted-foreground mb-0.5">
                          Reservation Requirement
                        </div>

                        <div
                          className={`rounded-xl border p-3.5 cursor-pointer transition-all ${
                            service.reservation_status === "needs_reservation" || service.reservation_status === "reservation_attached"
                              ? "border-blue-400/80 bg-blue-50/80"
                              : "border-border/80 bg-background/75 hover:border-blue-300/80 hover:bg-blue-50/40"
                          }`}
                          onClick={() => {
                            if (service.reservation_status === "pending_decision" || service.reservation_status === "no_reservation_required") {
                              handleStatusChange(service, "needs_reservation");
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              service.reservation_status === "needs_reservation" || service.reservation_status === "reservation_attached"
                                ? "border-blue-500"
                                : "border-muted-foreground"
                            }`}>
                              {(service.reservation_status === "needs_reservation" || service.reservation_status === "reservation_attached") && (
                                <div className="w-2 h-2 rounded-full bg-blue-500" />
                              )}
                            </div>
                            <span className="text-sm font-medium">This service DOES require a reservation</span>
                          </div>
                          
                          {(service.reservation_status === "needs_reservation" || service.reservation_status === "reservation_attached") && (
                            <div className="mt-3 space-y-2.5">
                              {(service.ticket_files ?? []).map((ticketFile) => (
                                <div
                                  key={ticketFile.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleDownloadReservation(service, ticketFile);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      void handleDownloadReservation(service, ticketFile);
                                    }
                                  }}
                                  className="group w-full rounded-lg border border-emerald-300/70 bg-emerald-50/65 p-2.5 text-left transition cursor-pointer hover:border-emerald-400 hover:bg-emerald-100/70"
                                >
                                  <div className="flex items-start gap-2.5">
                                    <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-emerald-300/70 bg-white/90 text-emerald-700">
                                      <FileText className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-start justify-between gap-2">
                                        <p className="text-[11px] uppercase tracking-wide text-emerald-800/80">
                                          Attached file
                                        </p>
                                        <button
                                          type="button"
                                          disabled={deletingFileId === ticketFile.id}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            void handleDeleteReservationFile(service, ticketFile);
                                          }}
                                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-emerald-800/70 transition hover:bg-emerald-200/70 hover:text-emerald-900 disabled:opacity-50"
                                          aria-label="Delete attached file"
                                          title="Delete file"
                                        >
                                          {deletingFileId === ticketFile.id ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                          ) : (
                                            <Trash2 className="h-3.5 w-3.5" />
                                          )}
                                        </button>
                                      </div>
                                      <p className="truncate text-sm font-medium text-emerald-900" title={ticketFile.file_name ?? undefined}>
                                        {ticketFile.file_name}
                                      </p>
                                      {ticketFile.uploaded_at && (
                                        <p className="mt-0.5 text-xs text-emerald-800/70">
                                          Uploaded on {format(new Date(ticketFile.uploaded_at), "MMM d, yyyy HH:mm")}
                                        </p>
                                      )}
                                      <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-700/90 group-hover:text-emerald-800">
                                        <Download className="h-3.5 w-3.5" />
                                        Click to download
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              <div
                                className={`rounded-lg border-2 border-dashed p-3 text-center transition cursor-pointer ${
                                  uploadingServiceId === service.id
                                    ? "border-blue-300 bg-blue-50/50"
                                    : draggingServiceId === service.id
                                      ? "border-blue-400 bg-blue-50"
                                      : "border-blue-300/70 bg-white/70 hover:border-blue-400 hover:bg-blue-50/60"
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInlineReservationPicker(service.id);
                                }}
                                onDragEnter={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDraggingServiceId(service.id);
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDraggingServiceId(service.id);
                                }}
                                onDragLeave={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDraggingServiceId((previous) => (previous === service.id ? null : previous));
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDraggingServiceId(null);
                                  void handleUploadReservation(service, e.dataTransfer.files || null);
                                }}
                              >
                                <input
                                  ref={(element) => {
                                    reservationInputRefs.current[service.id] = element;
                                  }}
                                  type="file"
                                  accept=".pdf,application/pdf"
                                  multiple
                                  className="hidden"
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    void handleUploadReservation(service, e.target.files || null);
                                    e.currentTarget.value = "";
                                  }}
                                />

                                {uploadingServiceId === service.id ? (
                                  <div className="flex items-center justify-center gap-2 text-sm font-medium text-blue-700">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Uploading reservation...
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-center gap-2 text-sm font-semibold text-blue-700">
                                      <Upload className="h-4 w-4" />
                                      {(service.ticket_files ?? []).length > 0
                                        ? "Add more files"
                                        : "Click to upload or drag and drop"}
                                    </div>
                                    <p className="text-xs text-blue-700/80">PDF only (max 10MB each)</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div
                          className={`rounded-xl border p-3.5 cursor-pointer transition-all ${
                            service.reservation_status === "no_reservation_required"
                              ? "border-slate-400/90 bg-slate-100/80"
                              : "border-border/80 bg-background/75 hover:border-slate-300/90 hover:bg-slate-100/60"
                          }`}
                          onClick={() => {
                            if (service.reservation_status !== "no_reservation_required") {
                              handleStatusChange(service, "no_reservation_required");
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              service.reservation_status === "no_reservation_required"
                                ? "border-slate-500"
                                : "border-muted-foreground"
                            }`}>
                              {service.reservation_status === "no_reservation_required" && (
                                <div className="w-2 h-2 rounded-full bg-slate-500" />
                              )}
                            </div>
                            <span className="text-sm font-medium">This service does NOT require a reservation</span>
                          </div>
                          
                          {service.reservation_status === "no_reservation_required" && (
                            <div className="mt-2 text-sm text-slate-600 flex items-center gap-1">
                              <AlertTriangle className="h-4 w-4" />
                              No reservation required — confirmed by operator
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {services.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No services in this booking package.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showConfirmChangeModal} onOpenChange={setShowConfirmChangeModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the reservation status for "{pendingStatusChange?.service.service_name}"?
              {pendingStatusChange?.service.reservation_status === "reservation_attached" && (
                <span className="block mt-2 text-amber-600">
                  Warning: This will remove the currently attached reservation file.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingStatusChange(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStatusChange}>
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default BookingPackagePage;
